import { useEffect, useRef } from 'react';

import { timezoneList } from '@repo/ui/constants/timezone';
import { useTimezone } from '@repo/ui/hooks/use-timezone';
import type { Timezone } from '@repo/ui/types/timezone';

import { EmptySearch } from '../EmptySearch/EmptySearch';
import { TimezoneListItem } from '../TimezoneListItem/TimezoneListItem';

import './TimezoneList.scss';

interface TimezoneListProps {
  search: string;
  onSelectTimezone: (timezone: Timezone) => void;
  selectedTime?: string;
}

/** The nearest ancestor that actually scrolls vertically, if any. */
function findScrollableAncestor(element: HTMLElement): HTMLElement | null {
  let current = element.parentElement;

  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }

  return null;
}

export function TimezoneList({ search, onSelectTimezone, selectedTime }: TimezoneListProps) {
  const { timezone: selectedTimezone } = useTimezone();

  const selectedRef = useRef<HTMLButtonElement | null>(null);

  const searchLower = search.toLowerCase();
  const filteredTimezones = timezoneList.filter(
    ({ code, offset, name, label }) =>
      code.toLowerCase().includes(searchLower) ||
      offset.toLowerCase().includes(searchLower) ||
      name.toLowerCase().includes(searchLower) ||
      label?.toLowerCase().includes(searchLower),
  );

  useEffect(() => {
    const selectedItem = selectedRef.current;
    if (!selectedItem) {
      return;
    }

    // Centre the selected row inside the dropdown's own scroller only.
    // scrollIntoView() walks every scrollable ancestor, so it also scrolls the
    // page/step container - which drags the trigger button out of view once a
    // timezone has been picked and there is a selected row to scroll to.
    const scroller = findScrollableAncestor(selectedItem);
    if (!scroller) {
      return;
    }

    const itemRect = selectedItem.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    scroller.scrollTop += itemRect.top - scrollerRect.top - (scrollerRect.height - itemRect.height) / 2;
  }, []);

  return (
    <div className="TimezoneList">
      {filteredTimezones.length > 0 ? (
        filteredTimezones.map(timezone => (
          <TimezoneListItem
            itemRef={timezone.iana === selectedTimezone ? selectedRef : null}
            isSelected={timezone.iana === (selectedTime || selectedTimezone)}
            timezone={timezone}
            key={timezone.name}
            onClick={() => onSelectTimezone(timezone)}
          />
        ))
      ) : (
        <EmptySearch />
      )}
    </div>
  );
}
